import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, ArrowRight } from "lucide-react";
import { BRAND } from "@/content/landing";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const { red: RED, gold: GOLD, charcoal: CHARCOAL } = BRAND.colors;
const CATEGORIES = ["Singer", "Actor", "Influencer", "Live host", "Content creator", "Celebrity", "Micro-influencer"];

export default function ArtistSignup() {
  const [form, setForm] = useState({ name: "", email: "", whatsapp: "", category: "Influencer", followers: "", instagram: "", tiktok: "", facebook: "", youtube: "", rate_note: "", bio: "" });
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2 || form.email.trim().length < 3) return toast.error("Name and email required.");
    setBusy(true);
    try {
      await axios.post(`${BACKEND}/api/artists/signup`, form);
      setDone(true);
    } catch (err) {
      toast.error("Something went wrong — try again.");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="min-h-screen grid place-items-center px-5" style={{ background: CHARCOAL, color: "white" }}>
        <div className="max-w-md text-center">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest font-semibold px-3 py-1.5 bg-white/10 border border-white/15">
            <Sparkles className="w-3.5 h-3.5" style={{ color: GOLD }} /> Received
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl mt-5 tracking-tight leading-[1.05]">
            Terima kasih! We&apos;ll be in touch.
          </h1>
          <p className="mt-4 text-white/80">Our team reviews all artist submissions within 24-48 hours. Watch your inbox and WhatsApp for the next step.</p>
          <a href="/" className="mt-6 inline-flex items-center gap-2 bg-white text-zinc-900 px-6 py-3 font-bold hover:bg-zinc-200 text-sm" data-testid="artist-done-home">← Back home</a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      <div className="border-b border-zinc-200" style={{ background: CHARCOAL, color: "white" }}>
        <div className="max-w-3xl mx-auto px-5 py-12">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest font-semibold px-3 py-1.5 bg-white/10 border border-white/15"><span className="w-1.5 h-1.5" style={{ background: GOLD }} /> Artist / Influencer</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl mt-4 tracking-tight leading-[1.05]">Get paid for your <span style={{ color: GOLD }}>reach.</span></h1>
          <p className="mt-3 text-white/85 max-w-xl">Malaysian brands are booking artists on MIRESCO for TikTok Lives, promo videos, booth appearances, and ambassador deals. Tell us about you.</p>
        </div>
      </div>

      <form onSubmit={submit} className="max-w-3xl mx-auto px-5 py-10 space-y-5" data-testid="artist-signup-form">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <F label="Full name" testid="artist-name" v={form.name} on={set("name")} required />
          <F label="Email" testid="artist-email" v={form.email} on={set("email")} type="email" required />
          <F label="WhatsApp" testid="artist-wa" v={form.whatsapp} on={set("whatsapp")} placeholder="+601..." />
          <div>
            <label className="overline text-zinc-500">Category</label>
            <select value={form.category} onChange={set("category")} data-testid="artist-category" className="mt-2 w-full border border-zinc-300 bg-white px-3 py-2 text-sm focus:border-[#C8102E] focus:outline-none">
              {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <F label="Total followers" testid="artist-followers" v={form.followers} on={set("followers")} placeholder="e.g. 45K, 250K, 1.2M" />
          <F label="Instagram" testid="artist-ig" v={form.instagram} on={set("instagram")} placeholder="@handle or URL" />
          <F label="TikTok" testid="artist-tt" v={form.tiktok} on={set("tiktok")} placeholder="@handle or URL" />
          <F label="Facebook" testid="artist-fb" v={form.facebook} on={set("facebook")} placeholder="page URL" />
          <F label="YouTube" testid="artist-yt" v={form.youtube} on={set("youtube")} placeholder="channel URL" />
        </div>
        <div>
          <label className="overline text-zinc-500">Rate / package notes</label>
          <textarea value={form.rate_note} onChange={set("rate_note")} maxLength={400} rows={2} data-testid="artist-rate" placeholder="Per video, per live, per event, or commission-based" className="mt-2 w-full border border-zinc-300 bg-white px-3 py-2 text-sm focus:border-[#C8102E] focus:outline-none resize-none" />
        </div>
        <div>
          <label className="overline text-zinc-500">Short bio</label>
          <textarea value={form.bio} onChange={set("bio")} maxLength={600} rows={3} data-testid="artist-bio" placeholder="Who you are, your audience, why brands should book you." className="mt-2 w-full border border-zinc-300 bg-white px-3 py-2 text-sm focus:border-[#C8102E] focus:outline-none resize-none" />
        </div>
        <button type="submit" disabled={busy} data-testid="artist-submit" className="inline-flex items-center gap-2 text-white px-6 py-3 font-bold text-sm hover:opacity-90 disabled:opacity-50" style={{ background: RED }}>
          {busy ? "Submitting…" : "Submit Application"} <ArrowRight className="w-4 h-4" />
        </button>
      </form>
    </div>
  );
}

function F({ label, v, on, testid, type = "text", placeholder = "", required }) {
  return (
    <div>
      <label className="overline text-zinc-500">{label}{required && <span className="text-[#C8102E]"> *</span>}</label>
      <input type={type} value={v} onChange={on} placeholder={placeholder} data-testid={testid} required={required} className="mt-2 w-full border border-zinc-300 bg-white px-3 py-2 text-sm focus:border-[#C8102E] focus:outline-none" />
    </div>
  );
}
